import { Routes } from '@angular/router';

import { clientResolver } from './core/models/clientResolver';
import { FunctionalType } from './core/models/blockedFunctionalType';
import { redirectEquipmentResolver } from './core/services/redirect-equipment-resolver.service';
import { redirectPlanResolver } from './core/services/redirect-plan-resolver.service';
import { ServiceFeatures } from './core/models/serviceFeatures';

import { ExpiredComponent } from './core/components/expired/expired.component';
import { ExecutionStandComponent } from './execution/pages/execution-stand/execution-stand.component';
import { TariffComparisonComponent } from './core/components/tariff-comparison/tariff-comparison.component';

import { demoGuard } from './core/guards/demo.guard';
import { availableGuard } from './core/guards/available.guard';
import { defaultGuard } from './core/guards/default.guard';
import { emptyAssortmentGuard } from './core/guards/empty-assortment.guard';
import { featureGuard } from './core/guards/feature.guard';
import { adminGuard } from './core/guards/admin.guard';
import { managerGuard } from './core/guards/manager.guard';
import { welcomeGuard } from './core/guards/welcome.guard';
import { accessGuard } from './core/guards/access.guard';
import { expiredGuard } from './core/guards/expired.guard';
import { DwUsersActivityEventResolver } from './core/services/dw-users-activity-event-resolver.service';
import { DwUserActivityNames } from './core/models/dwUserActivityNames';
import { systemGuard } from './core/guards/system.guard';

export const routes: Routes = [
	{
		path: 'c/:clientId',
		children: [
			{
				path: '',
				pathMatch: 'full',
				loadChildren: () => import(/* webpackChunkName: 'default' */ './default/default.module')
					.then((m) => m.DefaultModule),
				canMatch: [availableGuard, defaultGuard],
				canActivate: [emptyAssortmentGuard]
			},
			{
				path: 'dashboard',
				loadChildren: () => import(/* webpackChunkName: 'dashboard' */ './dashboard/dashboard.module')
					.then((m) => m.DashboardModule),
				canMatch: [availableGuard, featureGuard, adminGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					feature: ServiceFeatures.Tasks
				}
			},
			{
				path: 'tasks',
				loadChildren: () => import(/* webpackChunkName: 'tasks' */ './tasks/tasks.module')
					.then((m) => m.TasksModule),
				canMatch: [availableGuard, featureGuard, managerGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					feature: ServiceFeatures.PlanogramsExecution
				}
			},
			{
				path: 'home',
				loadChildren: () => import(/* webpackChunkName: 'home' */ './home/home.module')
					.then((m) => m.HomeModule),
				canMatch: [availableGuard, demoGuard, adminGuard],
				canActivate: [emptyAssortmentGuard]
			},
			{
				path: 'welcome',
				loadChildren: () => import(/* webpackChunkName: 'welcome' */ './welcome/welcome.module')
					.then((m) => m.WelcomeModule),
				canMatch: [availableGuard, welcomeGuard, adminGuard],
				canActivate: [emptyAssortmentGuard]
			},
			{
				path: 'plan',
				loadChildren: () => import(/* webpackChunkName: 'plan' */ './plan/plan.module')
					.then((m) => m.PlanModule),
				canMatch: [availableGuard, accessGuard, featureGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-plan',
					additions: ['edit', 'view'],
					feature: ServiceFeatures.StorePlans
				}
			},
			{
				path: 'stand',
				loadChildren: () => import(/* webpackChunkName: 'planogram' */ './planogram/planogram.module')
					.then((m) => m.PlanogramModule),
				canMatch: [availableGuard, accessGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-planogram',
					additions: ['edit', 'view']
				}
			},
			{
				path: 'equipment',
				loadChildren: () => import(/* webpackChunkName: 'equipment' */ './equipment/equipment.module')
					.then((m) => m.EquipmentModule),
				canMatch: [availableGuard, accessGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-instrument',
					additions: ['edit', 'view']
				}
			},
			{
				path: 'product',
				loadChildren: () => import(/* webpackChunkName: 'product' */ './product/product.module')
					.then((m) => m.ProductModule),
				canMatch: [availableGuard, accessGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-product',
					additions: ['parameters-edit', 'image-edit', 'view'],
					isProduct: true
				}
			},
			{
				path: 'analytic',
				loadChildren: () => import(/* webpackChunkName: 'analytics' */ './analytics/analytics.module')
					.then((m) => m.AnalyticsModule),
				canMatch: [availableGuard, accessGuard, featureGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-analytics',
					feature: ServiceFeatures.Analytics,
					blockedType: FunctionalType.Analytics
				}
			},
			{
				path: 'control',
				loadChildren: () => import(/* webpackChunkName: 'control' */ './control/control.module')
					.then((m) => m.ControlModule),
				canMatch: [availableGuard, accessGuard, featureGuard],
				canActivate: [emptyAssortmentGuard],
				data: {
					access: 'ph-control',
					feature: ServiceFeatures.ExecutionControl,
					blockedType: FunctionalType.Control
				}
			},
			{
				path: 'notifications/:id',
				component: ExecutionStandComponent,
				canMatch: [availableGuard, featureGuard, managerGuard],
				canActivate: [emptyAssortmentGuard],
				resolve: { dwEvent: DwUsersActivityEventResolver },
				data: {
					feature: ServiceFeatures.PlanogramsExecution,
					blockedType: FunctionalType.SendExecution,
					dwEvent: DwUserActivityNames.PlanogramExecution
				}
			},
			{
				path: 'expired',
				component: ExpiredComponent,
				canMatch: [expiredGuard]
			},
			{
				path: 'tariff-comparison',
				component: TariffComparisonComponent,
				canMatch: []
			},
			{
				path: 'planogram',
				redirectTo: 'plan',
				resolve: { event : redirectPlanResolver }
			},
			{
				path: 'instrument',
				redirectTo: 'equipment',
				resolve: { event: redirectEquipmentResolver }
			},
			{
				path: 'system',
				loadChildren: () => import(/* webpackChunkName: 'system' */ './system/system.module')
					.then((m) => m.SystemModule),
				canMatch: [systemGuard]
			},
			{ path: '**', redirectTo: '' }
		]
	},
	{
		path: '',
		resolve: { client: clientResolver },
		children: [
			{ path: '**', redirectTo: '' }
		]
	}
];